/**
 * AD-19: the one normalizer, and the version stamp that makes changing it survivable.
 *
 * Every blind index, every projection token and every query passes through {@link normalize}. That
 * is what makes `Muñoz` findable as `munoz` and what makes an HMAC over a name deterministic. It is
 * also why the version exists: the day this function changes, every index built by the previous
 * version stops matching, silently, and uniqueness constraints built on those indexes stop being
 * unique. So every stored index carries {@link NORMALIZE_VERSION} as its `index_version`, and a
 * rebuild is a query rather than an archaeology project.
 *
 * **The steps, in order, and why each is where it is.**
 *
 * 1. `NFKD`. Compatibility decomposition, not canonical: it folds the ligature `ﬁ` to `fi`, the
 *    full-width `Ａ` to `A`, the superscript `²` to `2` and the half-width katakana `ｶ` to `カ`,
 *    all of which are the same character to a person typing a name into a search box.
 * 2. Strip non-spacing and enclosing marks. After NFKD an accent is a separate combining
 *    character, so this is what makes `Álvarez` and `Alvarez` the same token. **Spacing combining
 *    marks are deliberately kept**: in the Indic scripts `\p{Mc}` carries vowel content, and
 *    dropping it merges names that are not the same name.
 * 3. Lower case. `toLowerCase`, not a full Unicode case fold: a full fold maps `ß` to `ss`, which
 *    would collide a name spelled with an eszett into one spelled with a double s. `ß` stays `ß`
 *    and the Turkish dotless `ı` stays `ı`, distinct from `i`.
 * 4. The whole fold runs twice. Lowering can reintroduce a combining mark (`İ` lowercases to
 *    `i` followed by U+0307 under Unicode's special casing rules), and a normalizer that is not
 *    idempotent produces a different index the second time a value is written. The property suite
 *    pins idempotence over generated input; the second pass is what makes it hold.
 * 5. Collapse whitespace runs to one space and trim. `  Ana   Maria  ` and `Ana Maria` are the
 *    same name typed by two people.
 *
 * Punctuation is deliberately NOT removed here. Splitting a value into tokens is the search
 * projection's job (AD-24), and a normalizer that also tokenized could not be used for a blind
 * index over a whole field value.
 *
 * Two consequences are worth stating because they are choices rather than accidents. Stripping
 * marks folds the Japanese `が` onto `カ`, exactly as it folds `ñ` onto `n`: an index that ignores
 * accents ignores every mark, and the alternative is a per-script rule nobody can keep correct.
 * And `toLowerCase` is locale-independent on purpose, so `I` folds to `i` everywhere rather than
 * to `ı` on a Turkish machine and `i` on a Spanish one; an index whose content depended on the
 * server's locale would stop matching the day the container moved.
 */
/**
 * The version stamp stored as `index_version` beside every blind index.
 *
 * **Raise this in the same commit as any change to {@link normalize}.** The committed golden
 * vectors refuse a behavioural change that leaves the number alone, and refuse a raised number
 * that changed no behaviour, so the two can never drift apart.
 */
export const NORMALIZE_VERSION = 1;
/** Non-spacing and enclosing marks. Spacing marks (`\p{Mc}`) are kept: see step 2 above. */
const STRIPPED_MARKS = /[\p{Mn}\p{Me}]/gu;
const WHITESPACE_RUN = /\s+/gu;
function fold(value) {
    return value.normalize('NFKD').replace(STRIPPED_MARKS, '').toLowerCase();
}
/**
 * The normalized form of a value, for blind indexing, search and duplicate detection.
 *
 * Idempotent: `normalize(normalize(x))` equals `normalize(x)` for every input.
 */
export function normalize(value) {
    return fold(fold(value)).replace(WHITESPACE_RUN, ' ').trim();
}
//# sourceMappingURL=normalize.js.map